import { TimezoneEnum } from "../Enums/TimezoneEnums";

/**
 * Normalizes a timezone string by converting enum keys to their values.
 * Handles cases where timezone is stored as enum key (e.g., "UTC_MINUS_12") instead of value (e.g., "UTC-12:00").
 * @param {string | null | undefined} timeZone - The timezone string to normalize.
 * @returns {string} The normalized timezone string, or "UTC" if invalid.
 */
const normalizeTimezone = (timeZone?: string | null): string => {
  if (!timeZone) {
    return "UTC";
  }

  // If it's already in the correct format (UTC+XX:XX or UTC-XX:XX), return as is
  if (/^UTC[+-]\d{1,2}:\d{2}$/.test(timeZone)) {
    return timeZone === "UTC+00:00" ? "UTC" : timeZone;
  }

  // If it's "UTC", return as is
  if (timeZone === "UTC") {
    return "UTC";
  }

  // Try to convert enum key to value (e.g., "UTC_MINUS_12" -> "UTC-12:00")
  if (timeZone.includes("_")) {
    const enumValue = TimezoneEnum[timeZone as keyof typeof TimezoneEnum];
    if (enumValue) {
      return enumValue === "UTC+00:00" ? "UTC" : enumValue;
    }
  }

  // Return as-is for IANA timezones (will be validated when used)
  return timeZone;
};

/**
 * Returns the calendar date (YYYY-MM-DD) for a given timestamp in the given timezone.
 * @param timestamp - Unix timestamp in seconds.
 * @param timeZone - IANA name, "UTC", or "UTC±HH:MM".
 * @returns Date string "YYYY-MM-DD" in that timezone.
 */
export const getDayKeyInTimezone = (
  timestamp: number,
  timeZone?: string | null,
): string => {
  const dateInMs = timestamp * 1000;
  const normalizedTimeZone = normalizeTimezone(timeZone);

  if (normalizedTimeZone.startsWith("UTC") && normalizedTimeZone !== "UTC") {
    const offsetMatch = normalizedTimeZone.match(/UTC([+-])(\d{1,2}):(\d{2})/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMinutes = sign * (hours * 60 + minutes);
      const dateInZone = new Date(dateInMs + offsetMinutes * 60 * 1000);
      return dateInZone.toLocaleDateString("en-CA", { timeZone: "UTC" });
    }
  }

  try {
    return new Date(dateInMs).toLocaleDateString("en-CA", {
      timeZone: normalizedTimeZone,
    });
  } catch {
    return new Date(dateInMs).toLocaleDateString("en-CA", { timeZone: "UTC" });
  }
};

/**
 * Converts a Unix timestamp (in seconds) to a local time string.
 * @param {number} timestamp - The Unix timestamp in seconds.
 * @returns {string} The formatted time string.
 */
export const getLocalTimeFromTimestamp = (
  timestamp: number,
  timeZone?: string | null,
): string => {
  const dateInMs = timestamp * 1000;
  const date = new Date(dateInMs);
  const normalizedTimeZone = normalizeTimezone(timeZone);

  // Handle UTC offsets manually (Intl doesn't support UTC offset strings)
  if (normalizedTimeZone.startsWith("UTC") && normalizedTimeZone !== "UTC") {
    const offsetMatch = normalizedTimeZone.match(/UTC([+-])(\d{1,2}):(\d{2})/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMinutes = sign * (hours * 60 + minutes);

      const dateInZone = new Date(date.getTime() + offsetMinutes * 60 * 1000);
      return dateInZone.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      });
    }
  }

  // Use Intl for IANA timezones or UTC
  try {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: normalizedTimeZone,
    });
  } catch (error) {
    console.warn(
      `Invalid timezone "${normalizedTimeZone}", falling back to UTC`,
    );
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });
  }
};

/**
 * Formats a date string to a day and month string.
 * @param {string} dateString - The date string to format.
 * @returns {string} The formatted day and month string.
 */
export const formatDayAndMonth = (
  dateString: string,
  timeZone?: string | null,
): string => {
  const date = new Date(dateString);
  const normalizedTimeZone = normalizeTimezone(timeZone);

  // Handle UTC offsets manually (Intl doesn't support UTC offset strings)
  if (normalizedTimeZone.startsWith("UTC") && normalizedTimeZone !== "UTC") {
    const offsetMatch = normalizedTimeZone.match(/UTC([+-])(\d{1,2}):(\d{2})/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMinutes = sign * (hours * 60 + minutes);

      const dateInZone = new Date(date.getTime() + offsetMinutes * 60 * 1000);
      const weekday = dateInZone.toLocaleDateString("en-US", {
        weekday: "long",
        timeZone: "UTC",
      });
      const day = dateInZone.getUTCDate().toString().padStart(2, "0");
      const month = dateInZone.toLocaleDateString("en-US", {
        month: "long",
        timeZone: "UTC",
      });

      let formattedDate = `${weekday}, ${month} ${day}`;
      formattedDate = formattedDate.replace(", ", ", ").replace(" de ", " ");
      formattedDate =
        formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
      return formattedDate;
    }
  }

  // Use Intl for IANA timezones or UTC
  try {
    let formattedDate = date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      timeZone: normalizedTimeZone,
    });
    formattedDate = formattedDate.replace(", ", ", ").replace(" de ", " ");
    formattedDate =
      formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    return formattedDate;
  } catch (error) {
    console.warn(
      `Invalid timezone "${normalizedTimeZone}", falling back to UTC`,
    );
    let formattedDate = date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      timeZone: "UTC",
    });
    formattedDate = formattedDate.replace(", ", ", ").replace(" de ", " ");
    formattedDate =
      formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    return formattedDate;
  }
};

/**
 * Formats a date string to a long string.
 * @param {string} dateString - The date string to format.
 * @returns {string} The formatted long string.
 */
export const formatDateLongString = (
  timestamp: number,
  timeZone?: string | null,
): string => {
  const date = new Date(timestamp * 1000);
  const normalizedTimeZone = normalizeTimezone(timeZone);

  // Handle UTC offsets manually (Intl doesn't support UTC offset strings)
  if (normalizedTimeZone.startsWith("UTC") && normalizedTimeZone !== "UTC") {
    const offsetMatch = normalizedTimeZone.match(/UTC([+-])(\d{1,2}):(\d{2})/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMinutes = sign * (hours * 60 + minutes);

      const dateInZone = new Date(date.getTime() + offsetMinutes * 60 * 1000);
      return dateInZone.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
    }
  }

  // Use Intl for IANA timezones or UTC
  try {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: normalizedTimeZone,
    });
  } catch (error) {
    console.warn(
      `Invalid timezone "${normalizedTimeZone}", falling back to UTC`,
    );
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  }
};

/**
 * Gets the current time in the given date time zone in milliseconds.
 * @param {string} timeZone - The time zone offset (e.g., "UTC+01:00" or "UTC-05:00") or IANA timezone.
 * @returns {number} The current time in the given date time zone in milliseconds.
 */
export const getNowInDateTimeZone = (timeZone?: string | null): number => {
  if (!timeZone) {
    return Date.now();
  }

  const normalizedTimeZone = normalizeTimezone(timeZone);

  if (normalizedTimeZone.startsWith("UTC")) {
    const offsetMatch = normalizedTimeZone.match(/UTC([+-])(\d{1,2}):(\d{2})/);
    if (offsetMatch) {
      const sign = offsetMatch[1] === "+" ? 1 : -1;
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3], 10);
      const offsetMinutes = sign * (hours * 60 + minutes);

      const now = new Date();
      const utcTime = now.getTime() + now.getTimezoneOffset() * 60 * 1000;

      return utcTime + offsetMinutes * 60 * 1000;
    }
  }

  try {
    const now = new Date();
    const nowInZoneString = now.toLocaleString("en-US", {
      timeZone: normalizedTimeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });

    return new Date(nowInZoneString).getTime();
  } catch (error) {
    console.error(error);
    return Date.now();
  }
};

/**
 * Converts a Unix timestamp (in seconds) to a time string in HH:MM format.
 * Uses UTC to ensure consistency with how timestamps are stored.
 * @param {number} timestamp - The Unix timestamp in seconds.
 * @returns {string} The formatted time string (HH:MM).
 */
export const timestampToTimeString = (
  timestamp: number | undefined,
): string => {
  if (!timestamp) {
    return "";
  }
  const date = new Date(timestamp * 1000);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Converts a Unix timestamp (in seconds) to a date string in MM/DD/YYYY format.
 * Uses UTC to ensure consistency with how timestamps are stored.
 * @param {number} timestamp - The Unix timestamp in seconds.
 * @returns {string} The formatted date string (MM/DD/YYYY).
 */
export const timestampToDateString = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Formats a Unix timestamp in seconds or milliseconds to MM/DD/YYYY.
 * Returns an em dash when the value is empty or invalid.
 */
export const formatTimestampDate = (value?: number): string => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "—";
  }

  const timestampInMs =
    value >= 1e12 ? Math.floor(value) : Math.floor(value * 1000);
  return new Date(timestampInMs).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

/**
 * Formats a Unix timestamp (seconds or ms) to `MM/DD/YYYY, HH:mm`.
 * Returns an em dash when the value is empty or invalid.
 */
export const formatTimestampDateTime = (value?: number): string => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "—";
  }

  const timestampInMs =
    value >= 1e12 ? Math.floor(value) : Math.floor(value * 1000);
  return new Date(timestampInMs).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/**
 * Formats a Unix timestamp (seconds or ms) to `MM/DD/YYYY, HH:mm:ss`.
 * Returns an em dash when the value is empty or invalid.
 */
export const formatTimestampDateTimePrecise = (value?: number): string => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "—";
  }

  const timestampInMs =
    value >= 1e12 ? Math.floor(value) : Math.floor(value * 1000);
  return new Date(timestampInMs).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * Parse SQL/Cosmos calendar dates (YYYY-MM-DD or ISO midnight UTC) without local TZ shift.
 */
export function parseCalendarDate(
  value: string | Date | undefined | null,
): Date | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(
      Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()),
    );
  }

  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(
      Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
    );
  }

  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    ),
  );
}

/** MM/DD/YYYY for date-only membership/financial fields stored as calendar dates. */
export function formatCalendarDateToString(
  value: string | Date | undefined | null,
): string {
  const date = parseCalendarDate(value);
  if (!date) {
    return "";
  }
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
}

/** Today's calendar date in the user's local timezone as YYYY-MM-DD. */
export function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * WSG fiscal year (legacy financial.asp): Aug 1 (FY-1) through Jul 31 (FY).
 * Parses the calendar date parts from YYYY-MM-DD without timezone conversion.
 */
export function fiscalYearFromIso(iso: string): number {
  const match = String(iso ?? "").trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return getCurrentFiscalYear();
  }
  const calendarYear = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(calendarYear) || !Number.isFinite(month)) {
    return getCurrentFiscalYear();
  }
  return month >= 8 ? calendarYear + 1 : calendarYear;
}

/**
 * Returns the current WSG fiscal year using the same rule as the legacy system.
 */
export function getCurrentFiscalYear(): number {
  const now = new Date();
  const year = now.getUTCFullYear();
  return now.getUTCMonth() >= 7 ? year + 1 : year;
}
